import { format, startOfDay, subDays } from "@-/com.npmjs.date-fns";
import {
  DAILY_REVIEW_WINDOW_DAYS,
  RETENTION_WEEKS,
  type LearnedStudyCardRow,
  type LearningStatsData,
  type LearningStatsReviewHistoryData,
  type ProgressStageKey,
} from "@-/dev.improve.haniverse.lib.learning-stats.entities";
import {
  getUserCardStage,
  getUserCardStageFromIntervalDays,
} from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import { addDailyRechargePoints, computeStreaks } from "#learning-stats-series";

type StageValues = Record<ProgressStageKey, number>;
const emptyStages = (): StageValues => ({ familiar: 0, mastered: 0, new: 0, proficient: 0 });

type StageEvent = { order: number; stage: ProgressStageKey; time: number };
type CardHistory = {
  events: StageEvent[];
  lastReview: { reviewedAt: string; scheduledDays: number } | null;
  mastered: number | null;
  naturalStage: ProgressStageKey;
  previousStage: ProgressStageKey | undefined;
  started: number;
  weight: number;
};

/**
 * Reads arrive chronologically, not as a lifetime array. Keep one pending review
 * per learned card: the NEXT log describes the result of that pending review.
 * Fixed card events (enrollment, imported last-review, manual mastery, today) are
 * merged in timestamp order. Only daily deltas survive a completed transition.
 */
export function createStageHistoryAccumulator(
  cards: readonly LearnedStudyCardRow[],
  now: Date,
  weights: ReadonlyMap<string, number> = new Map(),
) {
  const nowMs = now.getTime();
  const histories = new Map<string, CardHistory>();
  const days = new Map<string, { counts: StageValues; weights: StageValues }>();

  function event(history: CardHistory, time: number, stage: ProgressStageKey, order: number) {
    return Number.isFinite(time) && time <= nowMs
      ? {
          order,
          stage:
            history.mastered !== null && time >= history.mastered ? ("mastered" as const) : stage,
          time: Math.max(history.started, time),
        }
      : null;
  }

  for (const card of cards) {
    const started = new Date(card.startedStudyingAt).getTime();
    if (!Number.isFinite(started) || started > nowMs) {
      continue;
    }
    const history: CardHistory = {
      events: [],
      lastReview: null,
      mastered:
        card.manuallyMasteredAt === null ? null : new Date(card.manuallyMasteredAt).getTime(),
      naturalStage: getUserCardStage({
        fsrsCard: { lastReview: card.lastReview, scheduledDays: card.scheduledDays },
        manuallyMasteredAt: null,
      }),
      previousStage: undefined,
      started,
      weight: Math.max(0, weights.get(card.id) ?? 0),
    };
    const fixedEvents = [
      event(history, started, "new", 0),
      card.lastReview === null
        ? null
        : event(history, new Date(card.lastReview).getTime(), history.naturalStage, 2),
      history.mastered === null ? null : event(history, history.mastered, "mastered", 3),
      event(history, nowMs, card.stage, 4),
    ];
    history.events = fixedEvents
      .filter((item): item is StageEvent => item !== null)
      .sort((a, b) => a.time - b.time || a.order - b.order);
    histories.set(card.id, history);
  }

  function record(history: CardHistory, next: StageEvent) {
    const previous = history.previousStage;
    if (previous === next.stage) {
      return;
    }
    const dayKey = format(new Date(next.time), "yyyy-MM-dd");
    const day = days.get(dayKey) ?? { counts: emptyStages(), weights: emptyStages() };
    if (previous !== undefined) {
      day.counts[previous] -= 1;
      day.weights[previous] -= history.weight;
    }
    day.counts[next.stage] += 1;
    day.weights[next.stage] += history.weight;
    days.set(dayKey, day);
    history.previousStage = next.stage;
  }

  function recordReview(history: CardHistory, reviewedAt: string, stage: ProgressStageKey) {
    const review = event(history, new Date(reviewedAt).getTime(), stage, 1);
    if (review === null) {
      return;
    }
    while (history.events[0] !== undefined) {
      const next = history.events[0];
      if (next.time > review.time || (next.time === review.time && next.order > review.order)) {
        break;
      }
      history.events.shift();
      record(history, next);
    }
    record(history, review);
  }

  return {
    addReview(row: { cardId: string; reviewedAt: string; scheduledDays: number | null }) {
      const history = histories.get(row.cardId);
      // A missing/deleted FSRS log has never contributed a stage event. The
      // surrounding review counter still counts its user-card review.
      if (history === undefined || row.scheduledDays === null) {
        return;
      }
      if (history.lastReview !== null) {
        recordReview(
          history,
          history.lastReview.reviewedAt,
          getUserCardStageFromIntervalDays(row.scheduledDays),
        );
      }
      history.lastReview = { reviewedAt: row.reviewedAt, scheduledDays: row.scheduledDays };
    },
    finish() {
      for (const history of histories.values()) {
        if (history.lastReview !== null) {
          recordReview(history, history.lastReview.reviewedAt, history.naturalStage);
          history.lastReview = null;
        }
        for (const fixed of history.events) {
          record(history, fixed);
        }
        history.events.length = 0;
      }
      const counts = emptyStages();
      const weighted = emptyStages();
      const characterStageTimeline: LearningStatsReviewHistoryData["characterStageTimeline"] = [];
      const coverageStageTimeline: LearningStatsData["coverageStageTimeline"] = [];
      for (const [dayKey, delta] of [...days].sort(([a], [b]) => a.localeCompare(b))) {
        for (const stage of ["new", "familiar", "proficient", "mastered"] as const) {
          counts[stage] += delta.counts[stage];
          weighted[stage] = Math.max(0, weighted[stage] + delta.weights[stage]);
        }
        characterStageTimeline.push({
          charactersByStage: { ...counts },
          dayKey,
          learnedCount: Object.values(counts).reduce((sum, count) => sum + count, 0),
        });
        const divisor =
          Math.max(
            1_000_000,
            Object.values(weighted).reduce((sum, weight) => sum + weight, 0),
          ) / 100;
        coverageStageTimeline.push({
          coveragePercentByStage: {
            familiar: weighted.familiar / divisor,
            mastered: weighted.mastered / divisor,
            new: weighted.new / divisor,
            proficient: weighted.proficient / divisor,
          },
          dayKey,
        });
      }
      return { characterStageTimeline, coverageStageTimeline };
    },
  };
}

/** Counts retain one bucket per local day; retention keeps only six fixed weeks. */
export function createReviewCountsAccumulator(now: Date) {
  const todayStart = startOfDay(now);
  const todayKey = format(todayStart, "yyyy-MM-dd");
  const sevenDaysAgo = subDays(todayStart, 7).getTime();
  const thirtyDaysAgo = subDays(todayStart, 30).getTime();
  const daily = new Map<string, number>();
  const dailyRechargePoints = new Map<string, number>();
  const weeks = Array.from({ length: RETENTION_WEEKS }, (_, index) => {
    const end = subDays(todayStart, (RETENTION_WEEKS - index - 1) * 7);
    return { correct: 0, end: end.getTime(), reviews: 0, start: subDays(end, 7).getTime() };
  });
  let totalReviews = 0;
  let reviewsLast7d = 0;
  let recent30Correct = 0;
  let recent30Total = 0;
  return {
    addLog({ rating, review }: { rating: number; review: string }) {
      addDailyRechargePoints(dailyRechargePoints, { rating, reviewedAt: review });
      const time = new Date(review).getTime();
      const correct = rating >= 3 ? 1 : 0;
      for (const week of weeks) {
        if (time >= week.start && time < week.end) {
          week.reviews += 1;
          week.correct += correct;
          break;
        }
      }
      if (time >= thirtyDaysAgo) {
        recent30Total += 1;
        recent30Correct += correct;
      }
    },
    addReview(reviewedAt: string) {
      const date = new Date(reviewedAt);
      const dayKey = format(date, "yyyy-MM-dd");
      daily.set(dayKey, (daily.get(dayKey) ?? 0) + 1);
      totalReviews += 1;
      if (date.getTime() >= sevenDaysAgo) {
        reviewsLast7d += 1;
      }
    },
    finish() {
      const streaks = computeStreaks(dailyRechargePoints, todayStart);
      return {
        currentStreak: streaks.current,
        dailyRechargePoints: Array.from(dailyRechargePoints, ([day, points]) => ({
          day,
          points,
        })).sort((a, b) => a.day.localeCompare(b.day)),
        dailyReviewSeries: Array.from({ length: DAILY_REVIEW_WINDOW_DAYS }, (_, index) => {
          const offset = DAILY_REVIEW_WINDOW_DAYS - index - 1;
          const day = subDays(todayStart, offset);
          return {
            frontColor: offset === 0 ? "#2563EB" : "#93C5FD",
            label: format(day, "EEE"),
            value: daily.get(format(day, "yyyy-MM-dd")) ?? 0,
          };
        }),
        longestStreak: streaks.longest,
        rechargePointsToday: dailyRechargePoints.get(todayKey) ?? 0,
        retentionLast30dPercent:
          recent30Total === 0 ? null : Math.round((recent30Correct / recent30Total) * 100),
        retentionSeries: weeks.map((week, index) => ({
          label: `W${index + 1}`,
          reviews: week.reviews,
          value: week.reviews === 0 ? 0 : Math.round((week.correct / week.reviews) * 100),
        })),
        reviewedTodayCount: daily.get(todayKey) ?? 0,
        reviewsLast7d,
        totalReviews,
      };
    },
  };
}
